import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Typography } from '@mui/material';
import { useForm } from 'react-hook-form';

//Reducer
import { getReason, useReasonReducer } from '@/main/store/reason';
import { getCountriesUser, useCountryReducer } from '@/main/store/country';
import { ReasonTypeEnum } from '@/main/services/enum/ReasonEnum';

//Services
import {
  getDefaultCountryOptionDrop,
  getTranslateByKey
} from '@/main/services/utils';
import { convertStringForBoolean } from '@/main/services/utilsString';
import { convertImageToBase64 } from '@/main/services/utilsFile';

//Api
import { procurementApi } from '@/main/api';

//Types
import { KeyValue, KeyValueDefault } from '@/main/models/common';
import {
  ProcurementProcessStatusRequest,
  ReasonFilterRequest
} from '@/main/models/request';
import { ManageStatusItemsResponse } from '@/main/models/response';
import { IInfoReasonComponent } from '../infoOrder/typesInfoOrder';

//Components
import KogAutoCompleteControlled from '../../../../../components/shared/inputAutoComplete/autoCompleteControlled';
import KogInputTextControlledNew from '../../../../../components/shared/inputText/inputTextControlledNew';
import SecondaryButton from '../../../../../components/shared/button/secondaryButton';

//Style
import * as S from './styles';
import KOGFileUploader from '@/main/components/shared/fileUploader/fileUploader';

interface IReason {
  title?: string | null;
  idReasonType: number | null;
  closeModalReason: React.Dispatch<React.SetStateAction<boolean>>;
  infoReasonComponent: IInfoReasonComponent;
  handleSaveCustom: () => void;
  handleUpdateQuoteCountStatus: () => void;
  handleUpdateQuoteGridStatus: (
    guid: string,
    newStatus: number,
    newStatusSupplierName: string
  ) => void;
  setResultDataManageStatus: (data: ManageStatusItemsResponse | null) => void;
}
function ModalReason({
  title,
  idReasonType,
  closeModalReason,
  infoReasonComponent,
  handleSaveCustom,
  handleUpdateQuoteCountStatus,
  handleUpdateQuoteGridStatus,
  setResultDataManageStatus
}: IReason) {
  const dispatch = useDispatch();
  const reasonReducer = useReasonReducer();
  const countryReducer = useCountryReducer();

  const [selectedReason, setSelectedReason] = useState<KeyValueDefault | null>(
    null
  );
  const [observationReason, setObservationReason] = useState<string>('');
  const [files, setFiles] = useState<File[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const optionDefaultCountryDrop = getDefaultCountryOptionDrop(
    countryReducer.countriesUser.data
  );

  const isDisabledObservation = () =>
    convertStringForBoolean(selectedReason?.default);
  const showInputImage = () => idReasonType === ReasonTypeEnum.Part;
  const INITIAL_FORM_STATE_SAVE = {
    reason: null as KeyValue | KeyValueDefault | null,
    observation: ''
  };
  type IReasonForm = typeof INITIAL_FORM_STATE_SAVE;
  const methodSave = useForm<IReasonForm>({
    mode: 'onChange',
    defaultValues: INITIAL_FORM_STATE_SAVE
  });

  function onSubmitSave() {
    setIsLoading(true);
    switch (idReasonType) {
      case ReasonTypeEnum.Cancel:
        saveApiReasonCancel();
        break;
      case ReasonTypeEnum.Part:
        saveApiReasonReturn();
        break;
      default:
        setIsLoading(false);
        break;
    }
  }

  const procurementItems = {
    idProcurementStatusCompany:
      infoReasonComponent.idProcurementStatusCompany || 0,
    guidProcurementSupplierItem: infoReasonComponent.guidSupplierItem || ''
  };

  function handleCancel() {
    setSelectedReason(null);
    setObservationReason('');
    closeModalReason(false);
  }

  async function changeStatusCancelApi(
    request: ProcurementProcessStatusRequest
  ) {
    try {
      const responseApi = await procurementApi.processCancelStatus(request);
      if (responseApi.data.result) {
        const resData = responseApi.data.result;
        if (resData.orderNewStatusIdChanged) {
          handleUpdateQuoteCountStatus();
          handleUpdateQuoteGridStatus(
            infoReasonComponent.guidProcurement || '',
            resData.orderNewStatusIdChanged,
            resData.orderNewStatusNameChanged
          );
        }
        setResultDataManageStatus(resData);
      }
      handleSaveCustom();
    } catch (error) {
    } finally {
      setSelectedReason(null);
      setObservationReason('');
      setIsLoading(false);
      closeModalReason(false);
    }
  }

  function saveApiReasonCancel() {
    const request: ProcurementProcessStatusRequest = {
      guidProcurement: infoReasonComponent.guidProcurement || '',
      confirm: false,
      guidProcurementSupplier:
        infoReasonComponent.guidProcurementSupplier || '',
      guidReason: String(selectedReason?.key) || '',
      reasonDescription: observationReason || null,
      idProcurementStatusCompanyNext: 0,
      procurementSupplierItems: [procurementItems],
      deliveryBySupplier: infoReasonComponent.deliveryBySupplier
    };

    changeStatusCancelApi(request);
  }

  async function changeStatusReturnApi(
    request: ProcurementProcessStatusRequest
  ) {
    try {
      const responseApi = await procurementApi.processReturnStatus(request);
      if (responseApi.data.result) {
        const resData = responseApi.data.result;
        if (resData.orderNewStatusIdChanged) {
          handleUpdateQuoteCountStatus();
          handleUpdateQuoteGridStatus(
            infoReasonComponent.guidProcurement || '',
            resData.orderNewStatusIdChanged,
            resData.orderNewStatusNameChanged
          );
        }
        setResultDataManageStatus(resData);
      }
      handleSaveCustom();
    } catch (error) {
    } finally {
      setSelectedReason(null);
      setObservationReason('');
      setIsLoading(false);
      closeModalReason(false);
    }
  }

  async function saveApiReasonReturn() {
    const base64ImagesArray = await Promise.all(
      files.map(item => convertImageToBase64(item))
    );

    const imagesRequest = base64ImagesArray.map(item => ({
      imageUpload: `data:image/jpeg;base64,${item.base64Image}`,
      imageName: item.name
    }));

    const request: ProcurementProcessStatusRequest = {
      guidProcurement: infoReasonComponent.guidProcurement || '',
      confirm: false,
      guidProcurementSupplier:
        infoReasonComponent.guidProcurementSupplier || '',
      guidReason: String(selectedReason?.key) || '',
      reasonDescription: observationReason || null,
      idProcurementStatusCompanyNext: 0,
      procurementSupplierItems: [procurementItems],
      deliveryBySupplier: infoReasonComponent.deliveryBySupplier,
      imagesRequest
    };

    changeStatusReturnApi(request);
  }

  useEffect(() => {
    dispatch(getCountriesUser());
  }, []);

  useEffect(() => {
    if (optionDefaultCountryDrop !== null && idReasonType) {
      const requestReason: ReasonFilterRequest = {
        guidCountry: String(optionDefaultCountryDrop?.key),
        idReasonType
      };
      dispatch(getReason(requestReason));
    }
  }, [optionDefaultCountryDrop]);

  return (
    <S.Contant>
      {title && <Typography variant="h6">{title}</Typography>}
      <form onSubmit={methodSave.handleSubmit(onSubmitSave)} noValidate>
        <S.InputGroup id='id-reasonComponent-containerReason-label'>
          <KogAutoCompleteControlled
            id="id-reasonComponent-autocomplete-reason"
            className="ignore-click"
            required
            label={getTranslateByKey('information.reason')}
            nameCustom="reason"
            errorMessageCustom=""
            control={methodSave.control}
            setValueCustom={methodSave.setValue}
            options={reasonReducer.reason.data}
            isLoading={reasonReducer.reason.isLoading}
            rulesCustom={{ required: true }}
            hasErrorCustom={false}
            onChangeCustom={(optionSelected: KeyValueDefault | null) => {
              setSelectedReason(optionSelected);
              methodSave.setValue('reason', optionSelected);
            }}
          />
          {isDisabledObservation() && (
            <KogInputTextControlledNew
              id="id-reasonComponent-text-observation"
              control={methodSave.control}
              name="observation"
              onChangeCustom={e => setObservationReason(e)}
              label={getTranslateByKey('information.observation')}
              required
              errorMessageCustom={
                methodSave.formState.errors.observation?.type === 'required'
                  ? getTranslateByKey('information.observationRequired')
                  : ''
              }
              controllerProps={{ rules: { required: true } }}
            />
          )}
          {showInputImage() && (
            <KOGFileUploader
              isFullScreenDragDrop={true}
              files={files}
              setFiles={setFiles}
              typeFiles={'image'}
              maxFiles={5}
            />
          )}
        </S.InputGroup>
        <S.ButtonGroup>
          <SecondaryButton
            onClick={handleCancel}
            id="id-reasonComponent-button-cancel"
            disabled={isLoading}>
            {getTranslateByKey('information.back')}
          </SecondaryButton>
          <SecondaryButton
            type="submit"
            id="id-reasonComponent-button-save"
            isLoading={isLoading}>
            {getTranslateByKey('information.save')}
          </SecondaryButton>
        </S.ButtonGroup>
      </form>
    </S.Contant>
  );
}

export default ModalReason;
